/**
 * Choose Ramp (Provider) Modal
 * Displays REAL quotes from the aggregation engine in a ranked list.
 * Selected item gets an accent border outline, others get surface background.
 * Shows loading skeletons while quotes load, and an empty state if no quotes available.
 */

import { useState, useMemo } from 'react';
import { CloseButton } from '../ui/CloseButton';
import { SearchInput } from '../ui/SearchInput';
import { Skeleton } from '../ui/Skeleton';
import { useT } from '../../i18n';
import { getProviderIconUrl } from '../../data/cryptoData';
import type { Quote, QuotesResponse } from '@nowramp/sdk';
import type { TranslationKey } from '../../i18n/en';

export interface ChooseRampModalProps {
	onClose: () => void;
	onSelect: (quote: Quote) => void;
	/** Real quotes from the aggregation engine */
	quotes: QuotesResponse | null;
	/** Whether quotes are still loading */
	quotesLoading: boolean;
	/** The selected crypto currency code (for display) */
	cryptoCurrency: string;
	/** Currently selected gateway ID (outline border) */
	selectedGatewayId?: string | null;
	/** Base URL for static assets */
	assetBaseUrl?: string;
}

/** Map raw backend unavailability reasons to user-friendly i18n strings */
function friendlyReason(reason: string, t: (key: TranslationKey) => string): string {
	if (/payment method/i.test(reason)) return t('provider.unavailablePayment');
	if (/country|not supported by/i.test(reason)) return t('provider.unavailableCountry');
	if (/network/i.test(reason)) return t('provider.unavailableNetwork');
	if (/currency|fiat|crypto/i.test(reason)) return t('provider.unavailableCurrency');
	return t('provider.unavailable');
}

function BestPriceIcon() {
	return (
		<svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
			<path d="M8 1l2.12 4.3 4.74.69-3.43 3.34.81 4.72L8 11.77l-4.24 2.28.81-4.72L1.14 5.99l4.74-.69L8 1z" fill="var(--t-success)" />
		</svg>
	);
}

function RecommendedIcon() {
	return (
		<svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
			<path d="M14 7.5a6 6 0 11-12 0 6 6 0 0112 0z" stroke="var(--t-accent)" strokeWidth="1.5" />
			<path d="M5.5 8l2 2 3.5-4" stroke="var(--t-accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	);
}

function QuoteTag({ quote }: { quote: Quote }) {
	const t = useT();
	if (quote.isBestRate) {
		return (
			<div className="flex items-center gap-1">
				<BestPriceIcon />
				<span className="text-[11px] font-medium" style={{ color: 'var(--t-success)' }}>
					{t('modal.bestPrice')}
				</span>
			</div>
		);
	}
	if (quote.isRecommended) {
		return (
			<div className="flex items-center gap-1">
				<RecommendedIcon />
				<span className="text-[11px] font-medium" style={{ color: 'var(--t-accent)' }}>
					{t('modal.recommended')}
				</span>
			</div>
		);
	}
	if (quote.rank && quote.rank <= 3) {
		return (
			<div className="text-[11px]" style={{ color: 'var(--t-text-muted)' }}>
				#{quote.rank}
			</div>
		);
	}
	return null;
}

function ProviderIcon({ gatewayId, gatewayLogo, assetBaseUrl = '' }: { gatewayId: string; gatewayLogo?: string; assetBaseUrl?: string }) {
	const localSrc = getProviderIconUrl(gatewayId, assetBaseUrl);
	return (
		<span className="rounded-full shrink-0 overflow-hidden inline-flex items-center justify-center" style={{ width: 36, height: 36 }}>
			<img
				src={localSrc}
				alt=""
				width={36}
				height={36}
				className="rounded-full"
				style={{ width: 36, height: 36, objectFit: 'cover' }}
				onError={(e) => {
					if (gatewayLogo && (e.target as HTMLImageElement).src !== gatewayLogo) {
						(e.target as HTMLImageElement).src = gatewayLogo;
					}
				}}
			/>
		</span>
	);
}

function PercentageDiff({ pct }: { pct: number }) {
	if (pct === 0) return null;
	const color = pct > 0 ? 'var(--t-success)' : 'var(--t-error)';
	const sign = pct > 0 ? '+' : '';
	return (
		<span className="text-[11px] font-medium" style={{ color }}>
			{sign}{pct.toFixed(2)}%
		</span>
	);
}

function LoadingSkeletons() {
	return (
		<div className="space-y-3">
			{[0, 1, 2].map((i) => (
				<div
					key={i}
					className="w-full flex items-center gap-3.5 modal-item-in"
					style={{
						borderRadius: 5,
						height: 64,
						paddingLeft: 16,
						paddingRight: 16,
						background: 'var(--t-surface)',
						animationDelay: `${i * 45}ms`,
					}}
				>
					<Skeleton w={36} h={36} className="rounded-full shrink-0" />
					<div className="flex-1">
						<Skeleton w={90} h={14} className="mb-1.5" />
						<Skeleton w={60} h={12} />
					</div>
					<div className="text-right">
						<Skeleton w={40} h={10} className="mb-1.5" />
						<Skeleton w={80} h={14} />
					</div>
				</div>
			))}
		</div>
	);
}

export function ChooseRampModal({
	onClose,
	onSelect,
	quotes,
	quotesLoading,
	cryptoCurrency,
	selectedGatewayId,
	assetBaseUrl = '',
}: ChooseRampModalProps) {
	const t = useT();
	const [search, setSearch] = useState('');

	const filteredQuotes = useMemo(() => {
		if (!quotes?.quotes) return [];
		const raw = search.trim().toLowerCase();
		if (!raw) return quotes.quotes;
		return quotes.quotes.filter((q) => q.gatewayName.toLowerCase().includes(raw));
	}, [quotes, search]);

	// Best crypto amount for percentage diff calculation (skip zero-amount quotes)
	const bestCryptoAmount = useMemo(() => {
		const first = filteredQuotes.find((q) => parseFloat(q.cryptoAmount) > 0);
		return first ? parseFloat(first.cryptoAmount) : 0;
	}, [filteredQuotes]);

	return (
		<>
			<div className="flex justify-end px-5 pt-5">
				<CloseButton onClick={onClose} />
			</div>
			<h2
				className="text-center text-[17px] font-medium mt-2 mb-6"
				style={{ color: 'var(--t-text)', fontSize: 17, fontWeight: 500, marginTop: 8, marginBottom: 24 }}
			>
				{t('modal.chooseRamp')}
			</h2>
			<SearchInput value={search} onChange={setSearch} />
			<div className="flex-1 overflow-y-auto custom-scroll px-5 space-y-3">
				{quotesLoading ? (
					<LoadingSkeletons />
				) : filteredQuotes.length === 0 ? (
					<div className="text-center py-10 text-[14px]" style={{ color: 'var(--t-text-muted)' }}>
						{quotes && quotes.quotes.length === 0
							? t('modal.noQuotes')
							: t('modal.noProvidersFound')}
					</div>
				) : (
					filteredQuotes.map((quote, i) => {
						const isBest = i === 0;
						const isSelected = selectedGatewayId === quote.gatewayId;
						const receiveAmount = parseFloat(quote.cryptoAmount);
						const hasQuote = receiveAmount > 0;
						const displayAmount =
							receiveAmount >= 1 ? receiveAmount.toFixed(4) : receiveAmount.toPrecision(6);

						// Percentage difference from best quote
						const pctDiff = !isBest && bestCryptoAmount > 0
							? ((receiveAmount - bestCryptoAmount) / bestCryptoAmount) * 100
							: 0;

						return (
							<button
								key={quote.gatewayId}
								onClick={() => hasQuote && onSelect(quote)}
								className={`w-full flex items-center gap-3 transition-colors modal-item-in${hasQuote ? ' card-press hover-item' : ''}`}
								style={{
									borderRadius: 'var(--t-card-radius)',
									padding: '12px 16px',
									border: isSelected && hasQuote ? '1.5px solid var(--t-accent)' : '1.5px solid transparent',
									background: 'var(--t-surface)',
									opacity: hasQuote ? 1 : 0.45,
									cursor: hasQuote ? 'pointer' : 'default',
									animationDelay: `${Math.min(i * 45, 350)}ms`,
								}}
							>
								<ProviderIcon gatewayId={quote.gatewayId} gatewayLogo={quote.gatewayLogo} assetBaseUrl={assetBaseUrl} />
								<div className="text-left flex-1 min-w-0">
									<div className="font-medium text-[14px] leading-[18px]" style={{ color: 'var(--t-text)' }}>
										{quote.gatewayName}
									</div>
									{hasQuote && <QuoteTag quote={quote} />}
								</div>
								<div className="text-right shrink-0">
									{hasQuote ? (
										<>
											<div className="text-[11px] leading-[14px]" style={{ color: 'var(--t-text-secondary)' }}>
												{isBest ? t('receive.label') : <PercentageDiff pct={pctDiff} />}
											</div>
											<div className="font-medium text-[13px] leading-[18px]" style={{ color: 'var(--t-text)' }}>
												<span className="text-[10px] font-normal" style={{ color: 'var(--t-text-secondary)' }}>{cryptoCurrency}</span>{' '}{displayAmount}
											</div>
										</>
									) : (
										<span className="text-[11px]" style={{ color: 'var(--t-text-muted)' }}>
											{t('rate.noQuote')}
										</span>
									)}
								</div>
							</button>
						);
					})
				)}

				{/* Unavailable gateways */}
				{!quotesLoading && quotes?.unavailableGateways && quotes.unavailableGateways.length > 0 && (
					<div className="pt-2 space-y-1.5">
						{quotes.unavailableGateways.map((g, i) => (
							<div
								key={g.gatewayId}
								className="flex items-center gap-3 modal-item-in"
								style={{
									borderRadius: 'var(--t-card-radius)',
									padding: '10px 16px',
									background: 'var(--t-surface)',
									opacity: 0.45,
									animationDelay: `${Math.min((filteredQuotes.length + i) * 45, 500)}ms`,
								}}
							>
								<ProviderIcon gatewayId={g.gatewayId} assetBaseUrl={assetBaseUrl} />
								<div className="text-left flex-1 min-w-0">
									<div className="font-medium text-[14px] leading-[18px]" style={{ color: 'var(--t-text)' }}>
										{g.gatewayName}
									</div>
								</div>
								<div className="text-right shrink-0">
									<span className="text-[11px]" style={{ color: 'var(--t-text-muted)' }}>
										{friendlyReason(g.reason, t)}
									</span>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</>
	);
}
